import { chakra, forwardRef, HTMLChakraProps, useMergeRefs, useSafeLayoutEffect } from '@chakra-ui/react';
import { throttle } from 'lodash';
import { ReactNode, useRef, useState } from 'react';

const wrapperStyles = {
  position: 'relative' as any,
  mx: 'auto',
  overflow: 'hidden',
};

const shadowStyles = {
  position: 'absolute' as any,
  left: '0',
  zIndex: '1',
  w: '100%',
  h: '29px',
  pointerEvents: 'none' as any,
};

const defaultShadowStyles = { top: { opacity: 0 }, bottom: { opacity: 1 } };

const calculateShadowStyles = (wrapper, content) => {
  const contentScrollHeight = content.scrollHeight - wrapper.offsetHeight;
  const currentScroll = content.scrollTop / contentScrollHeight;

  if (currentScroll === Infinity || Number.isNaN(currentScroll)) {
    return defaultShadowStyles;
  }

  const top = { opacity: currentScroll };
  const bottom = { opacity: 1 - currentScroll };

  return { top, bottom };
};

export interface IScrollShadowsProps extends HTMLChakraProps<'div'> {
  overlay?: ReactNode;
}

export const ScrollShadows = forwardRef<IScrollShadowsProps, 'div'>(({ children, overlay, ...rest }, ref) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [shadowStyle, setShadowStyle] = useState(defaultShadowStyles);

  useSafeLayoutEffect(() => {
    const wrapper = contentRef.current;
    const content = contentRef.current;

    setShadowStyle(calculateShadowStyles(wrapper, content));

    const handler = throttle(() => setShadowStyle(calculateShadowStyles(wrapper, content)), 300);

    content.addEventListener('scroll', handler);

    return () => {
      content.removeEventListener('scroll', handler);
    };
  }, []);

  return (
    <chakra.div ref={wrapperRef} {...wrapperStyles} {...rest}>
      <chakra.div
        style={shadowStyle.top}
        {...shadowStyles}
        top="0"
        bg="linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.46) 100%)"
      />
      <chakra.div
        style={shadowStyle.bottom}
        {...shadowStyles}
        bottom="0"
        bg="linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.46) 100%)"
      />
      <chakra.div
        ref={useMergeRefs(contentRef, ref)}
        position="absolute"
        top="0"
        right="0"
        bottom="0"
        left="0"
        overflowY="scroll"
        zIndex={0}
      >
        {children}
      </chakra.div>

      {overlay}
    </chakra.div>
  );
});
